import React from 'react';
import { styled, useTheme } from '@mui/material/styles';

import Chip from '../../../Chip';
import { THREE_SECONDS } from '../../constants';
import {
  selectBackgroundColor,
  selectIsThemeColorDark,
  selectLegibleButtonOutlineColor,
  selectLegibleElementColor,
  selectLegibleTextColor,
} from '../../StepWrapper/selectors';
import { useStepContext } from '../../StepWrapper/StepWrapper';

interface OptionButtonProps {
  /** Boolean to determine if button is disabled */
  isDisabled?: boolean;
  /** Boolean to determine if the app is being used by a mobile device */
  isMobile?: boolean;
  /** Indicates whether last OptionButton */
  isProactiveIntent?: boolean;
  /** Boolean to determine if the button is selected */
  isSelected?: boolean;
  isSelectIntentStep?: boolean;
  /** Custom margin */
  margin?: string;
  /** Callback function for on click event */
  onClick: (text: string) => void;
  /** Custom padding */
  padding?: string;
  /** Text to display inside button */
  text: string;
  /** Url to open in a new tab */
  url?: string | null;
  /** Url target attribute */
  urlTarget?: string;
}

const OptionButton: React.FC<React.PropsWithChildren<OptionButtonProps>> = ({
  isDisabled,
  isMobile,
  isProactiveIntent,
  isSelected,
  isSelectIntentStep = false,
  margin,
  onClick,
  padding = '8px 2px',
  text,
  url,
  urlTarget,
}) => {
  const theme = useTheme();
  const context = useStepContext();
  const { themeColor } = context;
  const backgroundColor = selectBackgroundColor(context);
  const legibleTextColor = selectLegibleTextColor(context);
  const optionalClick = isDisabled ? null : onClick;
  const legibleElementColor = selectLegibleElementColor(context);
  const legibleButtonOutlineColor = selectLegibleButtonOutlineColor(context);
  const isThemeColorDark = selectIsThemeColorDark(context);

  const NewButton = (
    <Chip
      backgroundColor={
        isDisabled
          ? theme.palette.colors.white
          : isSelected
            ? themeColor
            : theme.palette.colors.white
      }
      color={
        isDisabled
          ? theme.palette.colors.grey['700']
          : isSelected
            ? legibleTextColor
            : legibleElementColor
      }
      disabled={isDisabled}
      fontSize='14px'
      hoverColor={legibleElementColor !== themeColor ? themeColor : undefined}
      label={text}
      onClick={() => {
        if (optionalClick) {
          optionalClick(text);
        }
      }}
      size='medium'
      sx={[
        {
          '&&.Mui-focusVisible': {
            backgroundColor: themeColor,
            color: legibleTextColor,
            ...(isProactiveIntent
              ? isThemeColorDark
                ? {
                    boxShadow: `0 0 0 4px ${theme.palette.colors.black}`, // outer indicator
                    outline: `2px ${theme.palette.colors.white} solid`, // inner indicator
                  }
                : // Inverse colors:
                  {
                    boxShadow: `0 0 0 4px ${theme.palette.colors.white}`, // outer indicator
                    outline: `${legibleButtonOutlineColor} solid 2px`, // inner indicator
                  }
              : {
                  outline: `${legibleButtonOutlineColor} solid 2px`,
                }),
          },
          '&&:hover': {
            backgroundColor: theme.palette.colors.white,
            borderColor: 'transparent',
            color: legibleElementColor,
            ...(isProactiveIntent
              ? {
                  boxShadow: `0 0 0 4px ${theme.palette.colors.white}`, // outer indicator
                  outline: `${legibleButtonOutlineColor} solid 2px`, // inner indicator
                }
              : {
                  outline: `${legibleButtonOutlineColor} solid 2px`,
                }),
          },
          '.MuiChip-label': {
            whiteSpace: 'normal',
          },
          ':last-child': {
            mb: isProactiveIntent ? '0' : null,
          },
          ':not(:first-child)': {
            ml: isSelectIntentStep ? '7px' : null,
          },
          ':not(:last-child)': {
            mr: isProactiveIntent || isSelectIntentStep ? null : '7px',
          },
          fontWeight: 500,
          height: 'auto',
          mb: '8px',
          minHeight: '36px',
          outline: '2px solid transparent',
          padding,
        },
        !!isSelected && {
          '&&:hover': {
            bgcolor: themeColor,
            color: legibleTextColor,
          },
        },
        !!margin && { margin },
      ]}
      variant='intent'
    />
  );

  if (url)
    return (
      <OptionButtonWrapper
        backgroundColor={backgroundColor}
        hoveredTextColor={legibleTextColor}
        href={url}
        isMobile={isMobile}
        onClick={e => {
          const target = e.currentTarget.getAttribute('target');
          if (e.currentTarget.getAttribute('target') !== '_parent') {
            return;
          }

          e.preventDefault();

          const href = e.currentTarget.getAttribute('href');
          if (href !== null && target !== null) {
            setTimeout(() => window.open(href, target), THREE_SECONDS);
          }
        }}
        target={urlTarget || '_blank'}
        textColor={theme.palette.text.primary}
        themeColor={themeColor}
      >
        {NewButton}
      </OptionButtonWrapper>
    );
  else return NewButton;
};

export default OptionButton;

const OptionButtonWrapper = styled('a')<{
  backgroundColor: string;
  hoveredTextColor: string;
  isMobile?: boolean;
  textColor: string;
  themeColor: string;
}>`
  text-decoration: none;
  &:not(:first-child) {
    margin-left: 7px;
  }
  &:last-child {
    margin-bottom: 0;
  }
  &:focus {
    outline: none;
  }
  &:focus-visible .MuiChip-root {
    background-color: ${props =>
      props.isMobile ? props.backgroundColor : props.themeColor};
    color: ${props =>
      props.isMobile ? props.textColor : props.hoveredTextColor};
  }
`;
